import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingBag, CalendarDays } from "lucide-react";
import {
  useAdminAuth,
  AdminLogin,
  AdminLoading,
  useNewItemAlert,
  ConfirmDelete,
} from "./AdminAuth";
import { DateGroups } from "./AdminListPage";
import { OrderEditModal } from "./OrderEditModal";
import AdminBar from "./AdminBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMenuPage() {
  const [user, setUser] = useAdminAuth();
  const [orders, setOrders] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);

  useNewItemAlert(orders.length, reservations.length);

  const load = useCallback(async () => {
    const [o, r] = await Promise.all([
      axios.get(`${API}/orders`, { withCredentials: true }),
      axios.get(`${API}/reservations`, { withCredentials: true }),
    ]);
    setOrders(o.data);
    setReservations(r.data);
  }, []);

  useEffect(() => {
    if (!user) return;
    load().catch(() => {});
    const id = setInterval(() => load().catch(() => {}), 15000);
    return () => clearInterval(id);
  }, [user, load]);

  const setOrderStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/orders/${id}/status`,
        { status },
        { withCredentials: true }
      );
      setOrders((prev) =>
        prev.map((o) => (o.id === id ? { ...o, status } : o))
      );
    } catch {
      toast.error("Impossible de mettre à jour le statut.");
    }
  };

  const setReservationStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/reservations/${id}/status`,
        { status },
        { withCredentials: true }
      );
      setReservations((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status } : r))
      );
    } catch {
      toast.error("Impossible de mettre à jour le statut.");
    }
  };

  const markPrinted = async (list) => {
    const ids = list.filter((x) => !x.printed).map((x) => x.id);
    if (!ids.length) return;
    try {
      await axios.post(
        `${API}/orders/mark-printed`,
        { ids },
        { withCredentials: true }
      );
      setOrders((prev) =>
        prev.map((x) => (ids.includes(x.id) ? { ...x, printed: true } : x))
      );
    } catch {
      toast.error("Impression enregistrée hors ligne impossible.");
    }
  };

  const [toDelete, setToDelete] = useState(null);

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/${toDelete.tipo}/${toDelete.id}`, {
        withCredentials: true,
      });
      if (toDelete.tipo === "orders")
        setOrders((p) => p.filter((x) => x.id !== toDelete.id));
      else setReservations((p) => p.filter((x) => x.id !== toDelete.id));
      toast.success("Supprimé.");
    } catch {
      toast.error("Impossible de supprimer.");
    }
    setToDelete(null);
  };

  if (user === null) return <AdminLoading />;

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F1EB] px-5">
        <AdminLogin onSuccess={setUser} title="Menu de Gestion" />
      </div>
    );
  }

  return (
    <div
      data-testid="admin-menu-page"
      className="min-h-screen bg-[#F4F1EB] text-[#102C1F]"
    >
      <AdminBar />
      <header className="bg-[#102C1F] text-[#F4F1EB]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <span className="font-display text-xl italic md:text-2xl">
            Menu — Commandes & Réservations
          </span>
          <Link
            data-testid="menu-dashboard-link"
            to="/admin"
            className="rounded-full border border-[#F4F1EB]/30 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#F4F1EB] hover:text-[#102C1F]"
          >
            Gestion
          </Link>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-14 px-5 py-12 md:px-10 lg:grid-cols-2">
        <section>
          <h2 className="mb-8 flex items-center gap-3 font-display text-2xl italic">
            <ShoppingBag size={20} /> Commandes ({orders.length})
          </h2>
          <DateGroups
            tipo="orders"
            items={orders}
            onStatus={setOrderStatus}
            onDelete={(id, name) => setToDelete({ tipo: "orders", id, name })}
            onPrinted={markPrinted}
            onEdit={setEditingOrder}
          />
        </section>
        <section>
          <h2 className="mb-8 flex items-center gap-3 font-display text-2xl italic">
            <CalendarDays size={20} /> Réservations ({reservations.length})
          </h2>
          <DateGroups
            tipo="reservations"
            items={reservations}
            onStatus={setReservationStatus}
            onDelete={(id, name) =>
              setToDelete({ tipo: "reservations", id, name })
            }
          />
        </section>
      </main>

      {editingOrder && (
        <OrderEditModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSaved={load}
        />
      )}

      <ConfirmDelete
        item={toDelete}
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
